import { forwardRef } from 'react';
import styled from 'styled-components';

import { BODY_TYPOGRAPHY_STYLES } from './types';

export const enum TextAlign {
  Left = 'start',
  Center = 'center',
  Right = 'end',
  Justify = 'justify',
}

export type TypographyStyledAs =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'bodyLargeDMSans'
  | 'bodyLargeSpaceGrotesk'
  | 'bodyDMSans'
  | 'bodySpaceGrotesk'
  | 'bodySmallDMSans'
  | 'bodySmallSpaceGrotesk'
  | 'bodyExtraSmallDMSans'
  | 'bodyExtraSmallSpaceGrotesk'
  | 'bodyExtraExtraSmallDMSans'
  | 'bodyExtraExtraSmallSpaceGrotesk';

// Re-export for backward compatibility
export type StyledAs = TypographyStyledAs;

const StyledTypography = styled.p<{
  $weight?: 400 | 500 | 600;
  $marginBottom?: string;
  $color?: string;
  $textAlign?: TextAlign;
}>`
  color: ${({ $color }) => $color || 'inherit'};
  font-style: normal;
  margin: 0;
  ${({ $marginBottom }) => ($marginBottom ? `margin-bottom: ${$marginBottom};` : null)}
  ${({ $textAlign }) => ($textAlign ? `text-align: ${$textAlign};` : null)}
  padding: 0;

  &.h1 {
    font-family: 'Space Grotesk', system-ui, Helvetica, Arial, sans-serif;
    font-size: 5.75rem;
    font-weight: 500;
    line-height: 100%;
    letter-spacing: -2.76px;
  }

  &.h2 {
    font-family: 'Space Grotesk', system-ui, Helvetica, Arial, sans-serif;
    font-size: 3.75rem;
    font-weight: 500;
    line-height: 100%;
    letter-spacing: -1.2px;
  }

  &.h3 {
    font-family: 'Space Grotesk', system-ui, Helvetica, Arial, sans-serif;
    font-size: 3rem;
    font-weight: 500;
    line-height: 100%;
    letter-spacing: -0.48px;
  }

  &.h4 {
    font-family: 'Space Grotesk', system-ui, Helvetica, Arial, sans-serif;
    font-size: 2.5rem;
    font-weight: 500;
    line-height: 110%;
    letter-spacing: -1.2px;
  }

  &.h5 {
    font-family: 'Space Grotesk', system-ui, Helvetica, Arial, sans-serif;
    font-size: 1.625rem;
    font-weight: 500;
    line-height: 123.077%;
    letter-spacing: -0.78px;
  }

  &.h6 {
    font-family: 'Space Grotesk', system-ui, Helvetica, Arial, sans-serif;
    font-size: 1.3125rem;
    font-weight: 500;
    line-height: 123.81%;
    letter-spacing: -0.42px;
  }

  &.bodyLargeDMSans,
  &.bodyLargeSpaceGrotesk {
    font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
    font-size: 1.0625rem;
    font-weight: 400;
    line-height: 141.176%;
  }

  &.bodyDMSans,
  &.bodySpaceGrotesk {
    font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
    font-size: 0.9375rem;
    font-weight: 400;
    line-height: 133.333%;
  }

  &.bodySmallDMSans,
  &.bodySmallSpaceGrotesk {
    font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 128.571%;
  }

  &.bodyExtraSmallDMSans,
  &.bodyExtraSmallSpaceGrotesk {
    font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
    font-size: 0.8125rem;
    font-weight: 400;
    line-height: 123.077%;
  }

  &.bodyExtraExtraSmallDMSans,
  &.bodyExtraExtraSmallSpaceGrotesk {
    font-family: 'DM Sans', system-ui, Helvetica, Arial, sans-serif;
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 116.667%;
  }

  &.bodyLargeSpaceGrotesk,
  &.bodySpaceGrotesk,
  &.bodySmallSpaceGrotesk,
  &.bodyExtraSmallSpaceGrotesk,
  &.bodyExtraExtraSmallSpaceGrotesk {
    font-family: 'Space Grotesk', system-ui, Helvetica, Arial, sans-serif;
  }

  // The default font weights above have higher specificity because they're defined in classes,
  // so we need to use !important to override them here.
  ${({ $weight }) => $weight && `font-weight: ${$weight} !important;`}
`;

export type TypographyProps = {
  children: React.ReactNode;
  className?: string;
  /**
   * The level of typography the text appears as. This will match the style specified in the design.
   *
   * Defaults to `bodyDMSans`.
   */
  styledAs?: TypographyStyledAs;
  /**
   * The semantic tag of the element rendered. This should match how the text behaves within the context of the page. For example, you can style a link as `bodySmallDMSans` but render it as an `a` tag.
   *
   * If unspecified, the `renderedAs` prop will default to matching `styledAs`.
   *
   * When working with headings, be sure not to skip levels. For example, an h2 should not be followed by an h4 (without an h3 in between) even if the next heading is styled like an h4. In that example, you can choose h4 for the `style` and h3 for `renderedAs`.
   */
  renderedAs?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p' | 'a' | 'button' | 'span' | 'div' | 'li' | 'label' | 'td' | 'th';
  weight?: 400 | 500 | 600;
  marginBottom?: string;
  textAlign?: TextAlign;
  color?: string;

  // Allow additional props like `id`, `href`, etc.
  [x: string]: any;
};

/**
 * Renders text with design system styles.
 */
export const Typography = forwardRef<HTMLElement, TypographyProps>(
  ({ children, className, styledAs, renderedAs, weight, marginBottom, color, textAlign, ...rest }, ref) => {
    const styleToUse = styledAs || 'bodyDMSans';

    let asToUse = renderedAs;
    if (!renderedAs) {
      if (styleToUse === 'h6' || BODY_TYPOGRAPHY_STYLES.includes(styleToUse as any)) {
        asToUse = 'p';
      } else {
        // For heading styles (h1-h5), use the style as the element
        asToUse = styleToUse as 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
      }
    }

    return (
      <StyledTypography
        ref={ref}
        as={asToUse}
        className={`${styleToUse} ${className ? className : ''}`}
        $weight={weight}
        $marginBottom={marginBottom}
        $color={color}
        $textAlign={textAlign}
        {...rest}
      >
        {children}
      </StyledTypography>
    );
  },
);
